import React, { useEffect, useRef, useState } from 'react'; 
import { useNavigate, useLocation, Link } from 'react-router-dom'; 
import { useAuth } from '../../contexts/AuthContext'; 
import { Button } from '../ui/button'; 
import { Input } from '../ui/input'; 
import { Label } from '../ui/label'; 
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'; 
import { Separator } from '../ui/separator'; 
import { useToast } from '../ui/use-toast'; 
import { Loader2, Mail, Lock } from 'lucide-react'; 
// import { Github } from 'lucide-react'; 
// import { FcGoogle } from 'react-icons/fc'; 
 
function fieldHasValue(input: HTMLInputElement | null): boolean { 
  if (!input) return false; 
  if (input.value.trim()) return true; 
  try { 
    return input.matches(':-webkit-autofill') || input.matches(':autofill'); 
  } catch { 
    return false; 
  } 
} 
 
export const LoginForm: React.FC = () => { 
  const emailRef = useRef<HTMLInputElement>(null); 
  const passwordRef = useRef<HTMLInputElement>(null); 
  const [emailReady, setEmailReady] = useState(false); 
  const [passwordReady, setPasswordReady] = useState(false); 
  const [isLoading, setIsLoading] = useState(false); 
  const [magicLinkLoading, setMagicLinkLoading] = useState(false); 
  const [magicLinkSent, setMagicLinkSent] = useState(false); 
  // const [oauthLoading, setOauthLoading] = useState<string | null>(null); 
 
  const { signIn, signInWithMagicLink } = useAuth(); 
  // const { signInWithOAuth, oauthConfig, isOAuthAvailable } = useAuth(); 
  const navigate = useNavigate(); 
  const location = useLocation(); 
  const { toast } = useToast(); 
 
  // Get the intended destination from navigation state 
  const from = location.state?.from?.pathname || '/dashboard'; 
 
  // Account OAuth (Google, GitHub, Microsoft) is disabled. 
  // const hasOAuth = oauthConfig.google || oauthConfig.github || oauthConfig.microsoft; 
 
  const refreshFields = () => { 
    setEmailReady(fieldHasValue(emailRef.current)); 
    setPasswordReady(fieldHasValue(passwordRef.current)); 
  }; 
 
  // Saved credentials are written by the browser, not by onChange. 
  // Inputs stay uncontrolled so that fill is not cleared. 
  useEffect(() => { 
    const timer = window.setInterval(refreshFields, 200); 
    const stop = window.setTimeout(() => window.clearInterval(timer), 5000); 
    return () => { 
      window.clearInterval(timer); 
      window.clearTimeout(stop); 
    }; 
  }, []); 
 
  const handleSubmit = async (e: React.FormEvent) => { 
    e.preventDefault(); 
    const email = emailRef.current?.value.trim() ?? ''; 
    const password = passwordRef.current?.value ?? ''; 
    setIsLoading(true); 
 
    try { 
      const { error } = await signIn(email, password); 
       
      if (error) { 
        toast({ 
          title: 'Login Failed', 
          description: error.message, 
          variant: 'destructive', 
        }); 
      } else { 
        toast({ 
          title: 'Login Successful', 
          description: 'Welcome back!', 
        }); 
        navigate(from, { replace: true }); 
      } 
    } catch (error) { 
      toast({ 
        title: 'Login Failed', 
        description: 'An unexpected error occurred. Please try again.', 
        variant: 'destructive', 
      }); 
    } finally { 
      setIsLoading(false); 
    } 
  }; 
 
  const handleMagicLink = async () => { 
    const trimmed = emailRef.current?.value.trim() ?? ''; 
    if (!trimmed || isLoading || magicLinkLoading) return; 
    setMagicLinkLoading(true); 
    try { 
      sessionStorage.setItem('og01_auth_next', from); 
      const { error } = await signInWithMagicLink(trimmed); 
      if (error) { 
        const hidden = /signups not allowed|user not found|otp_disabled/i.test(error.message || ''); 
        if (!hidden) { 
          sessionStorage.removeItem('og01_auth_next'); 
          toast({ 
            title: 'Magic link failed', 
            description: error.message, 
            variant: 'destructive', 
          }); 
          return; 
        } 
      } 
      setMagicLinkSent(true); 
      toast({ 
        title: 'Check your email', 
        description: 'If an account exists for that email, a sign-in link is on its way.', 
      }); 
    } catch { 
      sessionStorage.removeItem('og01_auth_next'); 
      toast({ 
        title: 'Magic link failed', 
        description: 'An unexpected error occurred. Please try again.', 
        variant: 'destructive', 
      }); 
    } finally { 
      setMagicLinkLoading(false); 
    } 
  }; 
 
  /* 
  const handleOAuthLogin = async (provider: 'google' | 'github') => { 
    if (!isOAuthAvailable(provider)) { 
      toast({ 
        title: 'OAuth Not Configured', 
        description: `${provider} authentication is not configured. Please contact support.`, 
        variant: 'destructive', 
      }); 
      return; 
    } 
 
    setOauthLoading(provider); 
     
    try { 
      const { error } = await signInWithOAuth(provider); 
       
      if (error) { 
        toast({ 
          title: 'OAuth Login Failed', 
          description: error.message, 
          variant: 'destructive', 
        }); 
      } 
      // Success will be handled by the auth state change in AuthContext 
    } catch (error) { 
      toast({ 
        title: 'OAuth Login Failed', 
        description: 'An unexpected error occurred. Please try again.', 
        variant: 'destructive', 
      }); 
    } finally { 
      setOauthLoading(null); 
    } 
  }; 
  */ 
 
  return ( 
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900"> 
      <Card className="w-full max-w-md"> 
        <CardHeader className="space-y-1"> 
          <CardTitle className="text-2xl text-center">Sign In</CardTitle> 
          <CardDescription className="text-center"> 
            Enter your credentials to access your account 
          </CardDescription> 
        </CardHeader> 
        <CardContent className="space-y-4"> 
          <form onSubmit={handleSubmit} className="space-y-4"> 
            <div className="space-y-2"> 
              <Label htmlFor="email">Email</Label> 
              <div className="relative"> 
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /> 
                <Input 
                  ref={emailRef} 
                  id="email" 
                  name="email" 
                  type="email" 
                  autoComplete="username" 
                  placeholder="Enter your email" 
                  onChange={refreshFields} 
                  onInput={refreshFields} 
                  required 
                  className="pl-9" 
                  disabled={isLoading} 
                /> 
              </div> 
            </div> 
            <div className="space-y-2"> 
              <Label htmlFor="password">Password</Label> 
              <div className="relative"> 
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /> 
                <Input 
                  ref={passwordRef} 
                  id="password" 
                  name="password" 
                  type="password" 
                  autoComplete="current-password" 
                  placeholder="Enter your password" 
                  onChange={refreshFields} 
                  onInput={refreshFields} 
                  required 
                  className="pl-9" 
                  disabled={isLoading} 
                /> 
              </div> 
            </div> 
            <Button  
              type="submit"  
              className="w-full"  
              disabled={isLoading || !emailReady || !passwordReady} 
            > 
              {isLoading ? ( 
                <> 
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                  Signing In... 
                </> 
              ) : ( 
                'Sign In' 
              )} 
            </Button> 
          </form> 
 
          <div className="relative"> 
            <div className="absolute inset-0 flex items-center"> 
              <Separator /> 
            </div> 
            <div className="relative flex justify-center text-xs uppercase"> 
              <span className="bg-background px-2 text-muted-foreground"> 
                Or 
              </span> 
            </div> 
          </div> 
 
          {magicLinkSent ? ( 
            <p role="status" className="text-sm text-center text-muted-foreground"> 
              If an account exists for that email, you will receive a sign-in link. Open it in this same browser. 
            </p> 
          ) : ( 
            <Button 
              type="button" 
              variant="outline" 
              className="w-full" 
              onClick={handleMagicLink} 
              disabled={isLoading || magicLinkLoading || !emailReady} 
            > 
              {magicLinkLoading ? ( 
                <> 
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                  Sending link... 
                </> 
              ) : ( 
                <> 
                  <Mail className="mr-2 h-4 w-4" /> 
                  Email me a sign-in link 
                </> 
              )} 
            </Button> 
          )} 
 
          {/* Account OAuth is disabled. Google Connect for Search Console / GA4 stays separate. 
          {hasOAuth && ( 
            <> 
              <div className="relative"> 
                <div className="absolute inset-0 flex items-center"> 
                  <Separator /> 
                </div> 
                <div className="relative flex justify-center text-xs uppercase"> 
                  <span className="bg-background px-2 text-muted-foreground"> 
                    Or continue with 
                  </span> 
                </div> 
              </div> 
 
              <div className="grid grid-cols-2 gap-4"> 
                {oauthConfig.google && ( 
                  <Button 
                    variant="outline" 
                    onClick={() => handleOAuthLogin('google')} 
                    disabled={isLoading || oauthLoading !== null} 
                  > 
                    {oauthLoading === 'google' ? ( 
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                    ) : ( 
                      <FcGoogle className="mr-2 h-4 w-4" /> 
                    )} 
                    Google 
                  </Button> 
                )} 
                {oauthConfig.github && ( 
                  <Button 
                    variant="outline" 
                    onClick={() => handleOAuthLogin('github')} 
                    disabled={isLoading || oauthLoading !== null} 
                  > 
                    {oauthLoading === 'github' ? ( 
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                    ) : ( 
                      <Github className="mr-2 h-4 w-4" /> 
                    )} 
                    GitHub 
                  </Button> 
                )} 
              </div> 
            </> 
          )} 
          */} 
        </CardContent> 
        <CardFooter className="flex flex-col space-y-2"> 
          <div className="text-sm text-center text-muted-foreground"> 
            <Link to="/auth/forgot-password" className="hover:text-primary underline"> 
              Forgot your password? 
            </Link> 
          </div> 
          <div className="text-sm text-center text-muted-foreground"> 
            Don't have an account?{' '} 
            <Link to="/auth/signup" className="hover:text-primary underline font-medium"> 
              Sign up 
            </Link> 
          </div> 
        </CardFooter> 
      </Card> 
    </div> 
  ); 
};